import { TimePicker } from "@tql/ui-components-react/kendo/dateinputs";
import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import { DatePicker } from "@tql/ui-components-react/tql/dateinputs";
import { useId, useState } from "react";
import { useParams } from "react-router";

import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";

import { usePostMarkDelivered } from "../../hooks/use-post-mark-delivered.ts";

export const MarkDeliveredModal = ({
	onClose,
	isOpen,
}: {
	onClose: () => void;
	isOpen: boolean;
}) => {
	const deliveredDateId = useId();
	const deliveredTimeId = useId();
	const params = useParams();
	const { markDelivered, isSavingMarkDelivered } = usePostMarkDelivered();

	const [deliveredDate, setDeliveredDate] = useState<Date | null>(null);
	const [deliveredTime, setDeliveredTime] = useState<Date | null>(null);
	const [submitted, setSubmitted] = useState(false);

	// Combine the selected date and time into a single instant so it can be
	// validated against "now" and sent as a UTC ISO string.
	const completedTime =
		deliveredDate && deliveredTime
			? new Date(
					deliveredDate.getFullYear(),
					deliveredDate.getMonth(),
					deliveredDate.getDate(),
					deliveredTime.getHours(),
					deliveredTime.getMinutes(),
					deliveredTime.getSeconds(),
				)
			: null;
	const isFuture = completedTime ? completedTime.getTime() > Date.now() : false;

	const handleClose = () => {
		setDeliveredDate(null);
		setDeliveredTime(null);
		setSubmitted(false);
		onClose();
	};

	const handleConfirm = () => {
		setSubmitted(true);
		if (!completedTime || isFuture) {
			return;
		}
		markDelivered({
			poNumber: params["poNumber"] ?? "",
			completedTime: completedTime.toISOString(),
		});
		handleClose();
	};

	if (!isOpen) {
		return null;
	}

	return (
		<TraxModal
			confirmText={"Confirm Delivery"}
			onCancel={handleClose}
			onClose={handleClose}
			onConfirm={handleConfirm}
			showButtonsDisabled={isSavingMarkDelivered}
			title={"LTL Delivered Details"}
			type={"confirmation"}
			width={"20%"}
		>
			<div>
				<Label editorId={deliveredDateId}>
					Delivered Date
					<span className={"required"}>&nbsp;*</span>
				</Label>
				<DatePicker
					id={deliveredDateId}
					max={new Date()}
					onChange={(event) => setDeliveredDate(event.target.value)}
					value={deliveredDate}
				/>
				<div>
					{submitted && !deliveredDate ? (
						<Error>Delivered date is required.</Error>
					) : null}
				</div>

				<Label editorId={deliveredTimeId}>
					Delivered Time
					<span className={"required"}>&nbsp;*</span>
				</Label>
				<TimePicker
					id={deliveredTimeId}
					onChange={(event) => setDeliveredTime(event.value)}
					value={deliveredTime}
				/>
				<div>
					{submitted && !deliveredTime ? (
						<Error>Delivered time is required.</Error>
					) : null}
				</div>

				{submitted && isFuture ? (
					<Error>Delivered date and time cannot be in the future.</Error>
				) : null}
			</div>
		</TraxModal>
	);
};
